import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import ActivePacksPanel from '../components/studios/ActivePacksPanel';
import ComplianceGuardrails from '../components/studios/ComplianceGuardrails';
import { checkCompliance } from '../utils/complianceEngine';

/**
 * EMZI NEXUS V6 — Phase B4: Live Monitor
 * Real-time agent conversation monitoring with compliance checks
 */
const LiveMonitor = () => {
  const navigate = useNavigate();
  const { isManagement, isAdmin } = useAuth();
  const [inputMode, setInputMode] = useState('manual'); // 'manual' or 'live'
  const [inputText, setInputText] = useState('');
  const [customerProfile, setCustomerProfile] = useState({
    name: '',
    age: '',
    language: 'MS',
    matchScore: 0
  });
  const [complianceFlags, setComplianceFlags] = useState([]);
  const [activePacks, setActivePacks] = useState([]);
  const [showActivePacksPanel, setShowActivePacksPanel] = useState(true);
  const [isLiveMonitoring, setIsLiveMonitoring] = useState(false);
  const textareaRef = useRef(null);
  const complianceCheckTimeoutRef = useRef(null);

  // Debounced compliance checking (800ms)
  useEffect(() => {
    if (inputText && inputText.trim().length > 10) {
      if (complianceCheckTimeoutRef.current) {
        clearTimeout(complianceCheckTimeoutRef.current);
      }

      complianceCheckTimeoutRef.current = setTimeout(() => {
        performComplianceCheck(inputText);
      }, 800);
    }

    return () => {
      if (complianceCheckTimeoutRef.current) {
        clearTimeout(complianceCheckTimeoutRef.current);
      }
    };
  }, [inputText]);

  const performComplianceCheck = async (text) => {
    try {
      const result = await checkCompliance(text, 'live', [], null);
      
      if (result.flagged_phrases && result.flagged_phrases.length > 0) {
        const flags = result.flagged_phrases.map(flag => ({
          id: Date.now() + Math.random(),
          phrase: flag.phrase,
          severity: flag.severity,
          alternative: flag.alternative,
          timestamp: new Date().toISOString(),
          dismissed: false
        }));
        setComplianceFlags(prev => [...prev, ...flags]);
      }
    } catch (err) {
      console.error('Error checking compliance:', err);
    }
  };

  const handleDismissFlag = (flagId) => {
    setComplianceFlags(prev =>
      prev.map(flag =>
        flag.id === flagId ? { ...flag, dismissed: true } : flag
      )
    );
  };

  const handleReviewFlag = (flagId) => {
    const flag = complianceFlags.find(f => f.id === flagId);
    if (flag && flag.alternative) {
      // Replace the flagged phrase with the alternative
      const newText = inputText.replace(
        new RegExp(flag.phrase, 'gi'),
        flag.alternative
      );
      setInputText(newText);
      handleDismissFlag(flagId);
    }
  };

  const handleMakeCompliant = () => {
    // Apply all alternatives for active flags
    let newText = inputText;
    complianceFlags.forEach(flag => {
      if (!flag.dismissed && flag.alternative) {
        newText = newText.replace(
          new RegExp(flag.phrase, 'gi'),
          flag.alternative
        );
      }
    });
    setInputText(newText);
    setComplianceFlags([]);
  };

  const handleSofterTone = () => {
    // Simple tone softening: remove urgency words
    let newText = inputText
      .replace(/must/gi, 'could')
      .replace(/now/gi, 'soon')
      .replace(/immediately/gi, 'at your convenience')
      .replace(/hurry/gi, 'consider')
      .replace(/don't miss/gi, 'you might enjoy');
    setInputText(newText);
  };

  const simulateLiveTranscript = () => {
    setIsLiveMonitoring(true);
    const mockTranscripts = [
      'Customer: Hi, I\'m interested in your health products.',
      'Agent: Great! Let me tell you about our amazing cure for diabetes.',
      'Customer: Is it FDA approved?',
      'Agent: It will cure all your health problems instantly!',
    ];

    let index = 0;
    const interval = setInterval(() => {
      if (index < mockTranscripts.length) {
        setInputText(prev => prev + '\n' + mockTranscripts[index]);
        index++;
      } else {
        clearInterval(interval);
        setIsLiveMonitoring(false);
      }
    }, 2000);
  };

  const activeFlags = complianceFlags.filter(f => !f.dismissed);

  return (
    <div className="nexus-content overflow-hidden bg-white">
      {/* EMZI Header */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
              <span className="text-white font-black text-2xl">🎙️</span>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-wide text-white">Live Monitor</h1>
              <p className="text-sm text-white font-medium">Real-time agent monitoring & compliance</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {(isManagement() || isAdmin()) && (
              <span className="text-white text-xs font-medium opacity-90" title="System certified under EMZI ISO-Aligned Compliance Protocol">
                {isLiveMonitoring ? '🔴 LIVE' : '🟢 ISO Compliance Active'}
              </span>
            )}
            <Button 
              onClick={() => navigate('/dashboard')} 
              variant="outline" 
              size="sm" 
              className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105"
            >
              ← Back
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-0" style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
        {/* Active Packs Panel - Phase B4 */}
        {showActivePacksPanel && (
          <div style={{ 
            width: '320px', 
            flexShrink: 0, 
            height: 'calc(100vh - 140px)', 
            position: 'sticky', 
            top: '12px',
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <ActivePacksPanel
              studioType="live"
              productIds={[]}
              campaignId={null}
              onPacksLoaded={(packs) => setActivePacks(packs)}
            />
          </div>
        )}
        
        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Audience Context Panel */}
          <Card className="shadow-lg border-0 mb-4" style={{boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'}}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <span style={{ fontSize: '18px' }}>👤</span>
                <CardTitle className="text-base font-bold text-gray-900">Audience Context</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Customer Name</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-2 py-1 text-sm border rounded"
                    placeholder="e.g., Sarah Ahmad"
                    value={customerProfile.name}
                    onChange={(e) => setCustomerProfile({...customerProfile, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Age</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-2 py-1 text-sm border rounded"
                    placeholder="e.g., 32"
                    value={customerProfile.age}
                    onChange={(e) => setCustomerProfile({...customerProfile, age: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Language</label>
                  <select
                    className="w-full mt-1 px-2 py-1 text-sm border rounded"
                    value={customerProfile.language}
                    onChange={(e) => setCustomerProfile({...customerProfile, language: e.target.value})}
                  >
                    <option value="EN">English</option>
                    <option value="MS">Malay</option>
                    <option value="ZH">Chinese</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Match Score</label>
                  <div className="w-full mt-1 px-2 py-1 text-sm border rounded bg-gray-50 flex items-center justify-between">
                    <span className="font-semibold text-emzi-blue">{customerProfile.matchScore}%</span>
                    <Badge variant="default" className="text-xs">AI</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Live Monitor Card */}
          <Card className="shadow-xl border-0" style={{boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)'}}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emzi-blue bg-opacity-10 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-emzi-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-gray-900 font-bold">Live Monitor</CardTitle>
                    <CardDescription className="text-sm text-gray-600">
                      Real-time conversation monitoring
                    </CardDescription>
                  </div>
                </div>
                
                {/* Input Mode Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={inputMode === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInputMode('manual')}
                    className="text-xs"
                  >
                    Manual Input
                  </Button>
                  <Button
                    variant={inputMode === 'live' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setInputMode('live');
                      if (!isLiveMonitoring) {
                        simulateLiveTranscript();
                      }
                    }}
                    className="text-xs"
                  >
                    Live Transcript (Mock)
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Transcript Input */}
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    {inputMode === 'manual' ? 'Agent Script / Notes' : 'Live Transcript (Mock)'}
                  </label>
                  <Textarea
                    ref={textareaRef}
                    placeholder={
                      inputMode === 'manual' 
                        ? 'Type your conversation script or notes here...' 
                        : 'Live transcript will appear here automatically...'
                    }
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    rows={8}
                    className="mt-2 font-mono text-sm"
                    disabled={inputMode === 'live' && isLiveMonitoring}
                  />
                </div>

                {/* Real-time Compliance Checking */}
                {inputText && (
                  <ComplianceGuardrails
                    text={inputText}
                    studioType="live"
                    productIds={[]}
                    campaignId={null}
                  />
                )}

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMakeCompliant}
                    disabled={activeFlags.length === 0}
                    className="text-xs"
                  >
                    ✅ Make Compliant
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSofterTone}
                    disabled={!inputText}
                    className="text-xs"
                  >
                    🤝 Softer Tone
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInputText('')}
                    disabled={!inputText}
                    className="text-xs"
                  >
                    🗑️ Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Flag Queue */}
          {activeFlags.length > 0 && (
            <Card className="shadow-lg border-0 mt-4" style={{boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'}}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: '18px' }}>🚩</span>
                    <CardTitle className="text-base font-bold text-gray-900">
                      Flag Queue ({activeFlags.length})
                    </CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setComplianceFlags([])}
                    className="text-xs text-gray-500"
                  >
                    Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {activeFlags.map(flag => (
                    <Alert key={flag.id} className="bg-red-50 border-red-200">
                      <AlertDescription>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="font-semibold text-sm text-red-700">
                              Flagged: "{flag.phrase}"
                            </div>
                            {flag.alternative && (
                              <div className="text-xs text-gray-600 mt-1">
                                💡 Suggestion: "{flag.alternative}"
                              </div>
                            )}
                            <div className="text-xs text-gray-400 mt-1">
                              {new Date(flag.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReviewFlag(flag.id)}
                              className="text-xs px-2 py-1"
                            >
                              Apply
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDismissFlag(flag.id)}
                              className="text-xs px-2 py-1"
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveMonitor;
